import axios, { AxiosResponse } from 'axios'
import { Plan } from '../Plans'
import { Category } from '../Categories'
import { StatesListProps, CitiesListProps } from '../components/page2/left/Steps/AdressInfo'
import { LogisticProps, UserConfigsProps } from '../InitReq'
import { CompaniesTelData } from '../Form'
import { VoucherParams } from '../components/page1/right/VoucherValidation'
import { PaymentCardParams } from '../utils/card/paymentCard'

class Requests {
  token: string | null = null
  contelApi: string = process.env.REACT_APP_CONTEL_API || ''

  // REQUISIÇÕES
  async requestApi<T, S>(
    endpoint: string,
    method: 'get' | 'post' = 'get',
    data?: T,
  ): Promise<S> {
    try {
      const response: AxiosResponse<S> = await axios({
        method,
        url: `${this.contelApi}${endpoint}`,
        data: data || null,
        headers: {
          'x-caller-url': window.location.hostname
        }
      })
      return response.data
    } catch (error: any) {
      console.error(`Erro na resposta da API: ${error.message}`)
      const enhancedError = new Error(`Erro na resposta da API: ${error.message}`)
      enhancedError.cause = error?.response?.data  // preserva o body aqui
      throw enhancedError
    }
  }

  // ACESSO INDICAÇÃO
  async accessIndication(id: string): Promise<void> {
    try {
      await this.requestApi<any, any>(`/indicacao/salvaAcesso/${id}`, 'post')
    } catch (error) {
      console.error('Erro na requisição accessIndication:', error)
      throw error
    }
  }

  // FUNÇÕES GET
  async voucherValidator(voucher: string): Promise<VoucherParams> {
    try {
      const response = await this.requestApi<any, any>(`/indicacao/validar-voucher/${voucher}`)
      return response
    } catch (error) {
      console.error('Erro na requisição voucherValidator:', error)
      throw error
    }
  }
  
  async currentIp(): Promise<string> {
    try {
      const { data } = await axios.get<any, any>('https://api.ipify.org')
      return data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error(`Erro na busca de ip: ${error.message}`)
        throw new Error(`Erro na busca de ip: ${error.message}`)
      } else {
        console.error(`Erro desconhecido: ${error}`)
        throw new Error('Erro desconhecido')
      }
    }
  }

  async categoriesList(): Promise<Category[]> {
    try {
      const response = await this.requestApi<any, any>('/planos-categorias')
      return response
    } catch (error) {
      console.error('Erro na requisição categoriesList:', error)
      throw error
    }
  }

  async plansList(): Promise<Plan[]> {
    try {
      const { data: plansArray } = await this.requestApi<any, any>('/planos')
      return plansArray
    } catch (error) {
      console.error('Erro na requisição plansList:', error)
      throw error
    }
  }

  async virtualStock(): Promise<any> {
    try {
      const { total } = await this.requestApi<any, any>('/linhas/estoque?tipo=esim&noData=true')
      return total
    } catch (error) {
      console.error('Erro na requisição virtualStock:', error)
      throw error
    }
  }

  async physicalStock(): Promise<any> {
    try {
      const { total } = await this.requestApi<any, any>('/linhas/estoque?tipo=fisico&noData=true')
      return total
    } catch (error) {
      console.error('Erro na requisição physicalStock:', error)
      throw error
    }
  }

  async statesList(): Promise<StatesListProps[]> {
    try {
      const { data } = await this.requestApi<any, any>('/estados')
      return data
    } catch (error) {
      console.error('Erro na requisição statesList:', error)
      throw error
    }
  }

  async citiesList(selectedState: string): Promise<CitiesListProps[]> {
    try {
      const { data } = await this.requestApi<any, any>(`/cidades/${selectedState}`)
      return data
    } catch (error) {
      console.error('Erro na requisição citiesList:', error)
      throw error
    }
  }

  async logistic(): Promise<LogisticProps> {
    try {
      const response = await this.requestApi<any, any>('/logistica')
      return response
    } catch (error) {
      console.error('Erro na requisição logistic:', error)
      throw error
    }
  }

  async userConfigs({id, tipo}: {id: string | number, tipo: string}): Promise<UserConfigsProps> {
    try {
      const response = await this.requestApi<any, any>(
        `/detalhes-venda?id=${id}&tipo=${tipo}`,
        'get'
      )
      return response
    } catch (error) {
      console.error('Erro na requisição userConfig:', error)
      throw error
    }
  }

  async pixAccept({ id, tipo }: { id: string | number, tipo: string }): Promise<any> {
    try {
      const { pago } = await this.requestApi<any, any>(
        `/detalhes-venda?id=${id}&tipo=${tipo}`,
        'get',
      )
      return { pago }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Erro na requisição pixAccept: ${error.message}`)
      } else {
        throw new Error('Erro na requisição pixAccept')
      }
    }
  }

  async cartSearch(id: string): Promise<any> {
    try {
      const { retorno, data } = await this.requestApi<any, any>(
        `/compra/obterCarrinho?token=${id}`,
        'get',
      )
      return { retorno, data }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Erro na requisição cartSearch: ${error.message}`)
      } else {
        throw new Error('Erro na requisição cartSearch')
      }
    }
  }

  async companiesTel(): Promise<CompaniesTelData> {
    try {
      const { retorno, data } = await this.requestApi<any, any>('/portabilidade/operadorasDisponiveis')
      return { retorno, data }
    } catch (error) {
      console.error('Erro na requisição companiesTel:', error)
      throw error
    }
  }

  async checkoutSummary(hash: string) {
    return axios.get(
      `${this.contelApi}/checkout/compra/${hash}`,
      {
        headers: { 'x-caller-url': window.location.hostname },
        validateStatus: () => true,
      }
    )
  }

  // FUNÇÕES POST
  async couponValidator(requestData: { cupom: string, plano: number, operacao: string }): Promise<any> {
    try {
      const response = await this.requestApi<any, any>(
        '/cupom/validar',
        'post',
        requestData
      )
      return response
    } catch (error) {
      console.error('Erro na requisição couponValidator:', error)
    }
  }

  async cepValidator(cep: any) {
    try {
      const requestData = { cep }
      const response = await this.requestApi<any, any>(
        '/validar/cep',
        'post',
        requestData
      )
      return response
    } catch (error) {
      console.error('Erro na requisição cepValidator:', error)
    }
  }

  async cpfValidator(): Promise<any> {
    try {
      const requestData = { cpf: '52309817848' }
      const { data, valido } = await this.requestApi<any, any>(
        '/validar/cpf',
        'post',
        requestData
      )
      return { data, valido }
    } catch (error) {
      console.error('Erro na requisição cpfValidator:', error)
      console.error('CPF inválido')
      throw new Error('CPF inválido')
    }
  }

  async cnpjValidator(): Promise<any> {
    try {
      const requestData = { cnpj: '02.068.812/0001-02' }
      const { data, valido } = await this.requestApi<any, any>(
        '/validar/cnpj',
        'post',
        requestData
      )
      return { data, valido }
    } catch (error) {
      console.error('Erro na requisição cnpjValidator:', error)
      console.error('CNPJ inválido')
      throw new Error('CNPJ inválido')
    }
  }

  async cardRegister(requestData: any) {
    try {
      const { retorno, mensagem, dados } = await this.requestApi<any, any>(
        '/app/usuario/cartaodecredito/cadastrar',
        'post',
        requestData
      )
      return { retorno, mensagem, dados }
    } catch (error: any) {
      const err = (error?.cause as any)?.error
      const apiMessage = err?.error || err || 'Erro na requisição cardRegister'

      const enhancedError = new Error(apiMessage)
      ;(enhancedError as any).apiError = err
      throw enhancedError
    }
  }

  async buyRegister(requestData: any, cardReq: boolean = false) {
    try {
      const response = await this.requestApi<any, any>(
        `/compra/solicitar${cardReq ? '/cartao' : ''}`,
        'post',
        requestData
      )
      return response
    } catch (error: any) {
      const err = (error?.cause as any)?.error
      const apiMessage = err?.error || err || 'Erro na requisição buyRegister'

      const enhancedError = new Error(apiMessage)
      ;(enhancedError as any).apiError = err
      throw enhancedError
    }
  }

  async activationReq(requestData: any, cardReq: boolean = false) {
    try {
      const response = await this.requestApi<any, any>(
        `/ativacao/solicitar${cardReq ? '/cartao' : ''}`,
        'post',
        requestData
      )

      return response
    } catch (error: any) {
      const err = (error?.cause as any)?.error
      const apiMessage = err?.error || err || 'Erro na requisição activationReq'

      const enhancedError = new Error(apiMessage)
      ;(enhancedError as any).apiError = err
      throw enhancedError
    }
  }

  async portability(requestData: any) {
    try {
      const { retorno, mensagem } = await this.requestApi<any, any>(
        '/portabilidade/pre-agendar',
        'post',
        requestData
      )
      return { retorno, mensagem }
    } catch (error: any) {
      const err = (error?.cause as any)?.error
      const apiMessage = err?.error || err || 'Erro na requisição portability'

      const enhancedError = new Error(apiMessage)
      ;(enhancedError as any).apiError = err
      throw enhancedError
    }
  }

  async cartRegister(requestData: any) {
    try {
      const response = await this.requestApi<any, any>(
        '/compra/salvarCarrinho',
        'post',
        requestData
      )
      return response
    } catch (error) {
      console.error(`Erro na requisição cartRegister: ${error}`)
      return { success: false }
    }
  }

  async cartUpdate(token: string) {
    try {
      await this.requestApi<any, any>(
        '/compra/atualizarCarrinho',
        'post',
        {token}
      )
    } catch (error) {
      console.error("Erro na requisição 'cartRegister': ", error)
    }
  }

  async otpValidate(requestData: { otp_session_token: string, codigo: string }) {
    return axios.post(
      `${this.contelApi}/checkout/otp/validar`,
      requestData,
      {
        headers: { 'x-caller-url': window.location.hostname },
        validateStatus: () => true,
      }
    )
  }
 
  async otpResend(requestData: { otp_session_token: string }) {
    return axios.post(
      `${this.contelApi}/checkout/otp/reenviar`,
      requestData,
      {
        headers: { 'x-caller-url': window.location.hostname },
        validateStatus: () => true,
      }
    )
  }
  
  async paymentCard(requestData: PaymentCardParams) {
    return axios.post(
      `${this.contelApi}/checkout/cartao/pagar`,
      requestData,
      {
        headers: { 'x-caller-url': window.location.hostname },
        validateStatus: () => true,
      }
    )
  }
}

const requests = new Requests()

export default requests
